import { useMemo, useState } from 'react';
import { buildCtaUrl } from '../utils/attribution.js';
import { demoLinkProps } from '../utils/calendly.js';

const APP_STORE = 'https://apps.shopify.com/account-editor';

const DEFAULTS = { vol: 2000, rate: 8, cost: 7.5, defl: 80 };
const MIN_PER_TICKET = 8; // minutes saved per deflected ticket
const HOURLY = 70; // loaded labor value per hour
const PLAN_COST = 99; // Growth plan / mo

const money = (v) => '$' + Math.round(v).toLocaleString('en-US');
const commas = (v) => Math.round(v).toLocaleString('en-US');

export default function RoiCalc() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: parseFloat(e.target.value) }));

  const r = useMemo(() => {
    const orders = v.vol;
    const rate = v.rate / 100;
    const cost = v.cost;
    const deflection = v.defl / 100;
    const deflected = orders * rate * deflection;
    const hoursMo = (deflected * MIN_PER_TICKET) / 60;
    const ticketCostSaved = deflected * cost;
    const savedMo = ticketCostSaved + hoursMo * HOURLY;
    return {
      saved: money(savedMo),
      annual: money(savedMo * 12),
      hours: Math.round(hoursMo) + ' hrs',
      x: (PLAN_COST > 0 ? savedMo / PLAN_COST : 0).toFixed(1) + '×',
      deflected: commas(deflected),
    };
  }, [v]);

  const costLabel = v.cost % 1 === 0 ? v.cost : v.cost.toFixed(1);

  return (
    <section className="roi">
      <div className="roi__container">
        <div className="roi__head">
          <span className="section-label">ROI Calculator</span>
          <h2 className="section-title">See what Account Editor saves you.</h2>
        </div>

        <div className="roi__calc">
          <form className="roi__inputs" onSubmit={(e) => e.preventDefault()}>
            <div className="roi__field">
              <div className="roi__field-top">
                <label className="roi__field-label" htmlFor="roiVol">Order volume</label>
                <span className="roi__field-val"><b>{commas(v.vol)}</b><span>/ mo</span></span>
              </div>
              <input type="range" id="roiVol" min="100" max="20000" step="100" value={v.vol} onChange={set('vol')} className="roi__range" />
              <p className="roi__field-desc">Orders processed across all your Shopify stores per month.</p>
            </div>

            <div className="roi__field">
              <div className="roi__field-top">
                <label className="roi__field-label" htmlFor="roiRate">Edit request rate</label>
                <span className="roi__field-val"><b>{v.rate}</b><span>%</span></span>
              </div>
              <input type="range" id="roiRate" min="0" max="30" step="1" value={v.rate} onChange={set('rate')} className="roi__range" />
              <p className="roi__field-desc">% of orders where a customer asks to change something post-purchase.</p>
            </div>

            <div className="roi__field">
              <div className="roi__field-top">
                <label className="roi__field-label" htmlFor="roiCost">Cost per support ticket</label>
                <span className="roi__field-val roi__field-val--money"><span>$</span><b>{costLabel}</b></span>
              </div>
              <input type="range" id="roiCost" min="1" max="30" step="0.5" value={v.cost} onChange={set('cost')} className="roi__range" />
              <p className="roi__field-desc">Loaded cost: agent time + tooling + context-switch overhead.</p>
            </div>

            <div className="roi__field">
              <div className="roi__field-top">
                <label className="roi__field-label" htmlFor="roiDefl">Deflection rate</label>
                <span className="roi__field-val"><b>{v.defl}</b><span>%</span></span>
              </div>
              <input type="range" id="roiDefl" min="0" max="100" step="1" value={v.defl} onChange={set('defl')} className="roi__range" />
              <p className="roi__field-desc">% of edit + address issues Account Editor resolves self-serve.</p>
            </div>
          </form>

          <div className="roi__result">
            <div className="roi__result-hero">
              <span className="roi__result-label">You save</span>
              <span className="roi__result-num"><span>{r.saved}</span><span className="roi__result-per">/ mo</span></span>
              <button type="button" className="roi__reset" onClick={() => setV(DEFAULTS)}>
                <i className="ph ph-arrow-counter-clockwise" aria-hidden="true" /> Reset to defaults
              </button>
            </div>

            <div className="roi__result-side">
              <div className="roi__result-stats">
                <div className="roi__rstat"><span className="roi__rstat-num">{r.annual}</span><span className="roi__rstat-label">Annual savings</span></div>
                <div className="roi__rstat"><span className="roi__rstat-num">{r.hours}</span><span className="roi__rstat-label">Support hrs saved / mo</span></div>
                <div className="roi__rstat"><span className="roi__rstat-num">{r.x}</span><span className="roi__rstat-label">ROI vs Growth plan</span></div>
                <div className="roi__rstat"><span className="roi__rstat-num">{r.deflected}</span><span className="roi__rstat-label">Edit tickets deflected / mo</span></div>
              </div>

              <div className="roi__result-ctas">
                <a href={APP_STORE} className="roi__cta-primary">Install Free on Shopify <i className="ph ph-arrow-right" aria-hidden="true" /></a>
                <a {...demoLinkProps('home-roi-demo')} className="roi__cta-ghost"><i className="ph ph-calendar-blank" aria-hidden="true" /> Book a Demo</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
